// Loop guard for the portal's AUTOMATIC login redirects. Same guard as the TMS shell.
//
// Every automatic redirect leaves the page and comes back through Auth0, so the guard has to
// survive a full page load: it lives in sessionStorage (per tab, gone when the tab closes).
// It stores a count together with the timestamp of the first redirect in the current window,
// rather than a bare counter, so a count left over from an earlier failure in the same tab
// expires on its own instead of blocking a later, legitimate login.
//
// Explicit retries ("Try again") and successful logins reset it.
const STORAGE_KEY = 'customerPortal.loginRedirectGuard';

/** Automatic redirects allowed inside one window before the portal stops and shows the error. */
export const MAX_AUTOMATIC_REDIRECTS = 2;

/** Length of the window the redirects are counted in. */
export const REDIRECT_GUARD_WINDOW_MS = 60_000;

interface RedirectGuardState {
  count: number;
  windowStart: number;
}

// The try/catch blocks below are deliberate: sessionStorage access throws in some
// private-browsing modes and when storage is disabled. A guard that cannot persist fails open,
// so a storage failure never prevents a login.
const readGuard = (): RedirectGuardState | undefined => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
    if (
      typeof parsed?.count === 'number' &&
      typeof parsed?.windowStart === 'number'
    ) {
      return parsed;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

const writeGuard = (state: RedirectGuardState) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable: the guard cannot persist, the redirect still goes ahead.
  }
};

/**
 * Records an automatic login redirect if the guard still allows one.
 * Returns false when the limit for the current window has been reached.
 */
export const claimAutomaticRedirect = (now: number = Date.now()): boolean => {
  const current = readGuard();
  const inWindow =
    current !== undefined &&
    now - current.windowStart < REDIRECT_GUARD_WINDOW_MS;
  const count = inWindow ? current.count : 0;

  if (count >= MAX_AUTOMATIC_REDIRECTS) {
    return false;
  }

  writeGuard({
    count: count + 1,
    windowStart: inWindow ? current.windowStart : now
  });
  return true;
};

export const resetRedirectGuard = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing was persisted, so there is nothing to reset.
  }
};
